import { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { VotingPowerData } from "~/hooks/useVotingPowerData"

interface VotingPowerGraphProps {
  votingPowerData: VotingPowerData[]
  width?: number
  height?: number
  currentEpoch?: number
}

export function VotingPowerGraph({
  votingPowerData,
  width = 600,
  height = 400,
  currentEpoch = 0
}: VotingPowerGraphProps) {
  const [extended, setExtended] = useState(false)
  const [extensionFactor, setExtensionFactor] = useState(0)
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  
  // Enhanced padding for the graph to ensure content stays within bounds
  const padding = { top: 30, right: 40, bottom: 50, left: 60 }
  const graphWidth = width - padding.left - padding.right
  const graphHeight = height - padding.top - padding.bottom
  
  // Calculate effective staked tokens for a data point based on current epoch
  const calculateEffectiveStakedTokens = (data: VotingPowerData) => {
    // If original stake epoch is less than current epoch, it's completely depleted
    if (data.epoch <= currentEpoch) {
      return 0
    }
    
    // Get the original stake amount
    const originalStakedTokens = data.stakedTokens
    
    // Calculate remaining proportion based on epochs
    // As currentEpoch approaches data.epoch, effective tokens approach 0
    const totalEpochs = data.epoch
    const remainingEpochs = data.epoch - currentEpoch
    const proportion = remainingEpochs / totalEpochs
    
    // Return effective staked tokens
    return originalStakedTokens * proportion
  }
  
  // Calculate voting power for a data point considering current epoch
  const calculateVotingPower = (data: VotingPowerData) => {
    // Get effective staked tokens
    const effectiveStakedTokens = calculateEffectiveStakedTokens(data)
    
    // If no tokens remain, no voting power
    if (effectiveStakedTokens <= 0) {
      return 0
    }
    
    // Remaining epochs from current epoch
    const remainingEpochs = data.epoch - currentEpoch
    
    // Triangle area calculation
    return (remainingEpochs * effectiveStakedTokens) / 2
  }
  
  // Sort data by epoch
  const sortedData = [...votingPowerData].sort((a, b) => a.epoch - b.epoch)
  
  // Get active stakes (with remaining tokens)
  const activeStakes = sortedData.filter(data => calculateEffectiveStakedTokens(data) > 0)
  
  // Calculate the stacked token values at each unique epoch for plotting
  const uniqueEpochs = [...new Set([
    currentEpoch,
    ...activeStakes.map(data => data.epoch)
  ])].sort((a, b) => a - b)
  
  const stackedEpochData = uniqueEpochs.map(epoch => {
    // All data points that are active at this epoch
    const activeAtEpoch = activeStakes.filter(data => data.epoch > epoch)
    
    // Calculate total effective tokens at this epoch
    const totalTokens = activeAtEpoch.reduce((sum, data) => {
      const effectiveTokens = calculateEffectiveStakedTokens(data)
      const adjustedEffectiveTokens = epoch < data.epoch 
        ? effectiveTokens * ((data.epoch - epoch) / (data.epoch - currentEpoch)) 
        : 0
      return sum + adjustedEffectiveTokens
    }, 0)
    
    return {
      epoch,
      totalTokens,
      dataPoints: activeAtEpoch
    }
  })
  
  // Find maximum stake epoch for calculating extension
  const maxStakeEpoch = Math.max(
    currentEpoch + 1,
    ...activeStakes.map(data => data.epoch)
  )
  
  // Calculate the extended epoch based on the extension factor (integers only)
  const extendedEpoch = maxStakeEpoch + Math.round(extensionFactor)
  
  // Calculate the maximum values for scaling with integer epochs in mind
  const maxEpoch = Math.ceil(
    Math.max(
      currentEpoch + 1,
      ...uniqueEpochs,
      extended ? extendedEpoch : 0
    )
  ) + 1 // Add 1 for padding and ensure it's an integer
  
  const minEpoch = Math.floor(currentEpoch)
  
  // Calculate max tokens with buffer to avoid clipping
  const maxCalculatedTokens = stackedEpochData.length > 0 
    ? Math.max(...stackedEpochData.map(d => d.totalTokens), 1) 
    : 3
  
  // Calculate total tokens (sum of all original staked tokens)
  const totalOriginalTokens = votingPowerData.reduce((sum, data) => {
    return sum + data.stakedTokens
  }, 0)
  
  // Use max of original tokens for scaling to ensure consistent view
  // Make sure to include totalOriginalTokens in the calculation to ensure it's visible
  const maxOriginalTokens = Math.max(...votingPowerData.map(d => d.stakedTokens), 1)
  const maxStakedTokens = Math.max(maxCalculatedTokens, maxOriginalTokens, totalOriginalTokens) * 1.1 // 10% buffer for headroom
  
  // Calculate current effective tokens (sum of all tokens based on current epoch)
  const totalEffectiveTokens = votingPowerData.reduce((sum, data) => {
    return sum + calculateEffectiveStakedTokens(data)
  }, 0)
  
  // Calculate integer epochs to display (from currentEpoch to maxEpoch)
  const visibleEpochs = Array.from(
    { length: maxEpoch - minEpoch + 1 }, 
    (_, i) => minEpoch + i
  ).filter(epoch => epoch >= currentEpoch)
  
  // Scale factors - using integer-based scaling for x-axis
  const scaleX = (epoch: number) => {
    // Convert epoch to discrete integer position
    const epochRange = maxEpoch - currentEpoch
    const position = (epoch - currentEpoch) / epochRange
    return position * graphWidth
  }
  
  const scaleY = (tokens: number) => {
    return graphHeight - (tokens / maxStakedTokens) * graphHeight
  }
  
  // Calculate current total voting power
  const currentVotingPower = activeStakes.reduce((sum, data) => {
    return sum + calculateVotingPower(data)
  }, 0)
  
  // Calculate extended voting power (for the extended restake option)
  const calculateExtendedVotingPower = () => {
    // If no active stakes, return 0
    if (activeStakes.length === 0) return 0
    
    // Find the stake with the maximum staked tokens (highest y-value) 
    const maxTokensStake = [...activeStakes].reduce((max, current) => {
      const effectiveTokens = calculateEffectiveStakedTokens(current)
      return effectiveTokens > calculateEffectiveStakedTokens(max) ? current : max
    }, activeStakes[0])
    
    const maxEffectiveTokens = calculateEffectiveStakedTokens(maxTokensStake)
    
    // Calculate the additional voting power from extending with a diagonal line
    // This is the area of the triangle from max y-value to extended epoch on x-axis
    const additionalArea = (maxEffectiveTokens * (extendedEpoch - maxTokensStake.epoch)) / 2
    
    // Return the current voting power plus the additional area
    return currentVotingPower + additionalArea
  }
  
  const extendedVotingPower = calculateExtendedVotingPower()
  const votingPowerIncrease = extendedVotingPower - currentVotingPower
  const votingPowerIncreasePercent = currentVotingPower > 0 
    ? Math.round((votingPowerIncrease / currentVotingPower) * 100) 
    : 0
  
  // Generate a stacked area path for the voting power data
  const generateStackedAreaPath = () => {
    if (stackedEpochData.length <= 1) return "" // Need at least two points for a path
    
    let path = ""
    
    // Start at the bottom right of the first point (current epoch, 0)
    path += `M ${scaleX(currentEpoch)},${graphHeight} `
    
    // Add line segments for each epoch (going up)
    for (const point of stackedEpochData) {
      // Ensure we're within bounds
      const boundedX = Math.min(scaleX(point.epoch), graphWidth)
      const boundedY = Math.max(scaleY(point.totalTokens), 0)
      path += `L ${boundedX},${boundedY} `
    }
    
    // Now go back from right to left along the bottom
    // Again ensuring we stay within bounds
    const lastPoint = stackedEpochData[stackedEpochData.length - 1]
    const boundedLastX = Math.min(scaleX(lastPoint.epoch), graphWidth)
    
    path += `L ${boundedLastX},${graphHeight} `
    path += `L ${scaleX(currentEpoch)},${graphHeight} Z`
    
    return path
  }
  
  // Generate extension path - line from the maximum staked tokens point to the extended point on x-axis
  const generateExtensionPath = () => {
    if (activeStakes.length === 0) return ""
    
    // Get the coordinates of the maximum staked tokens point
    const maxTokensPoint = {
      x: scaleX(currentEpoch),
      y: scaleY(maxCalculatedTokens)
    }
    
    // Get the coordinates of the extended point (on x-axis)
    const extendedPoint = {
      x: scaleX(extendedEpoch),
      y: graphHeight // Bottom of the graph (on x-axis)
    }
    
    // Create a triangular path: from maxPoint -> to extendedPoint -> to basePoint -> back to maxPoint
    const basePoint = {
      x: maxTokensPoint.x,
      y: graphHeight
    }
    
    // Return a closed triangular path
    return `M ${maxTokensPoint.x},${maxTokensPoint.y} L ${extendedPoint.x},${extendedPoint.y} L ${basePoint.x},${basePoint.y} Z`
  }
  
  // Use integer-based x-axis ticks (only integers from currentEpoch to maxEpoch)
  // Select a reasonable number of ticks to display based on available space
  const calculateXAxisTicks = () => {
    // Get all integer epochs from currentEpoch to maxEpoch
    const allEpochs = visibleEpochs
    
    // If we have a manageable number, display all of them
    if (allEpochs.length <= 10) {
      return allEpochs
    }
    
    // Otherwise, select a subset that distributes evenly
    const step = Math.ceil(allEpochs.length / 10)
    return allEpochs.filter((_, i) => i % step === 0)
  }
  
  const xAxisTicks = calculateXAxisTicks()
  
  // Generate y-axis ticks with nice round numbers
  const generateYAxisTicks = () => {
    const tickCount = 6
    const maxValue = maxStakedTokens
    
    // Calculate a nice tick interval
    const roughInterval = maxValue / (tickCount - 1)
    
    // Round to a nice number
    let tickInterval
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
    
    if (roughInterval / magnitude < 1.5) {
      tickInterval = magnitude
    } else if (roughInterval / magnitude < 3.5) {
      tickInterval = 2 * magnitude
    } else if (roughInterval / magnitude < 7.5) {
      tickInterval = 5 * magnitude
    } else {
      tickInterval = 10 * magnitude
    }
    
    // Generate ticks from 0 to maxValue
    const ticks = []
    for (let i = 0; i <= maxValue; i += tickInterval) {
      ticks.push(i)
    }
    
    // Make sure the last tick includes maxValue
    if (ticks[ticks.length - 1] < maxValue) {
      ticks.push(ticks[ticks.length - 1] + tickInterval)
    }
    
    return ticks
  }
  
  const yAxisTicks = generateYAxisTicks()
  
  // Function to ensure tooltip doesn't go off screen
  const getTooltipPosition = (pointX: number, pointY: number, width: number, height: number) => {
    // Default positions
    let x = pointX + 10
    let y = pointY - 60
    
    // Check if tooltip would go off right edge
    if (x + width > graphWidth) {
      x = pointX - width - 10
    }
    
    // Check if tooltip would go off top edge
    if (y < 0) {
      y = 10
    }
    
    // Check if tooltip would go off bottom edge
    if (y + height > graphHeight) {
      y = graphHeight - height - 10
    }
    
    return { x, y }
  }
  
  // Calculate minimum and maximum values for the extension slider
  const minExtensionValue = 0 // Minimum one epoch extension
  const maxExtensionValue = 10 // Maximum ten epochs extension
  
  // Calculate token percentage
  const tokenPercentage = totalOriginalTokens > 0 
    ? Math.round((totalEffectiveTokens / totalOriginalTokens) * 100) 
    : 0
  
  // Check if current tokens and total tokens are approximately equal
  // Using a small epsilon to account for floating point precision
  const isCurrentEqualTotal = Math.abs(totalEffectiveTokens - totalOriginalTokens) < 0.01
  
  return (
    <div className="flex flex-col gap-4">
      {/* Display voting power information */}
      <div className="w-full flex items-center justify-between px-4 py-2 bg-black/20 rounded-lg">
        <div className="flex flex-col">
          <span className="text-white text-sm opacity-70">Current Voting Power</span>
          <span className="text-white text-xl font-semibold">{currentVotingPower.toFixed(2)}</span>
        </div>
        
        {extended && (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/50">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>

            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-white text-sm opacity-70">After Restake</span>
                <div className="flex items-center gap-2">
                  <span className="text-white text-xl font-semibold">{extendedVotingPower.toFixed(2)}</span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-[#60e25e]/20 text-[#60e25e]">
                    +{votingPowerIncreasePercent}%
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <div className="relative border border-white/10 rounded-lg p-4 bg-[#080c0d]">
        <svg 
          ref={svgRef}
          width={width} 
          height={height}
          className="overflow-visible"
        >
          {/* Graph content container with padding applied */}
          <g transform={`translate(${padding.left}, ${padding.top})`}>
            {/* Grid lines */}
            {xAxisTicks.map((tick, i) => {
              const x = scaleX(tick)
              if (x < 0 || x > graphWidth) return null
              
              return (
                <line
                  key={`x-grid-${i}`}
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={graphHeight}
                  stroke="white"
                  strokeOpacity={0.1}
                  strokeDasharray="2,2"
                />
              )
            })}
            {yAxisTicks.map((tick, i) => {
              const y = scaleY(tick)
              if (y < 0 || y > graphHeight) return null
              
              return (
                <line
                  key={`y-grid-${i}`}
                  x1={0}
                  y1={y}
                  x2={graphWidth}
                  y2={y}
                  stroke="white"
                  strokeOpacity={0.1}
                  strokeDasharray="2,2"
                />
              )
            })}
            
            {/* Only show Total Tokens line when it's different from Current Tokens */}
            {!isCurrentEqualTotal && (
              <>
                <line
                  x1={0}
                  y1={scaleY(totalOriginalTokens)}
                  x2={graphWidth}
                  y2={scaleY(totalOriginalTokens)}
                  stroke="#FFD700" // Gold color for original tokens
                  strokeWidth={1.5}
                  strokeDasharray="5,3"
                />
                <text
                  x={graphWidth - 5}
                  y={scaleY(totalOriginalTokens) - 6}
                  textAnchor="end"
                  fill="#FFD700"
                  fontSize={11}
                  fontWeight="bold"
                >
                  Total Staked: {totalOriginalTokens.toFixed(2)}
                </text>
              </>
            )}
            
            {/* Current Effective Tokens horizontal line */}
            <line
              x1={0}
              y1={scaleY(totalEffectiveTokens)}
              x2={graphWidth}
              y2={scaleY(totalEffectiveTokens)}
              stroke={isCurrentEqualTotal ? "#FFD700" : "#FF6B6B"}
              strokeWidth={1.5}
              strokeDasharray="5,3"
            />
            <text
              x={graphWidth - 5}
              y={scaleY(totalEffectiveTokens) - 6}
              textAnchor="end"
              fill={isCurrentEqualTotal ? "#FFD700" : "#FF6B6B"}
              fontSize={11}
              fontWeight="bold"
            >
              {isCurrentEqualTotal 
                ? `Total Staked: ${totalOriginalTokens.toFixed(2)}`
                : `Staked: ${totalEffectiveTokens.toFixed(2)} (${tokenPercentage}%)`
              }
            </text>
            
            {/* Current epoch indicator - keep the line but remove the text */}
            <line
              x1={scaleX(currentEpoch)}
              y1={0}
              x2={scaleX(currentEpoch)}
              y2={graphHeight}
              stroke="#FF6B6B"
              strokeWidth={2}
              strokeOpacity={0.8}
            />
            
            {/* Extension triangle (filled with lower opacity) */}
            {extended && (
              <motion.path
                key="extension-triangle"
                d={generateExtensionPath()}
                fill="#60e25e"
                fillOpacity={0.2}
                stroke="#60e25e"
                strokeWidth={2}
                strokeDasharray="5,5"
                strokeOpacity={0.7}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            )}
            
            {/* Stacked area chart - always show this */}
            <motion.path
              key="stacked-area"
              d={generateStackedAreaPath()}
              fill="#60e25e"
              fillOpacity={0.4}
              stroke="#60e25e"
              strokeWidth={2}
              strokeOpacity={0.7}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
            {/* X and Y axes */}
            <line 
              x1={0} 
              y1={graphHeight} 
              x2={graphWidth} 
              y2={graphHeight} 
              stroke="white" 
              strokeOpacity={0.6}
            />
            <line 
              x1={0} 
              y1={0} 
              x2={0} 
              y2={graphHeight} 
              stroke="white" 
              strokeOpacity={0.6}
            />
            
            {/* X-axis ticks - integer epochs only */}
            {xAxisTicks.map((tick, i) => {
              const x = scaleX(tick)
              if (x < 0 || x > graphWidth) return null
              
              return (
                <g key={`x-tick-${i}`}>
                  <line
                    x1={x}
                    y1={graphHeight}
                    x2={x}
                    y2={graphHeight + 5}
                    stroke="white"
                    strokeOpacity={0.6}
                  />
                  <text
                    x={x}
                    y={graphHeight + 20}
                    textAnchor="middle"
                    fill="white"
                    fontSize={12}
                    opacity={0.8}
                  >
                    {tick} {/* Display exact integer epoch value */}
                  </text>
                </g>
              )
            })}
            
            {/* Y-axis ticks */}
            {yAxisTicks.map((tick, i) => {
              const y = scaleY(tick)
              if (y < 0 || y > graphHeight) return null
              
              return (
                <g key={`y-tick-${i}`}>
                  <line
                    x1={-5}
                    y1={y}
                    x2={0}
                    y2={y}
                    stroke="white"
                    strokeOpacity={0.6}
                  />
                  <text
                    x={-10}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={12}
                    opacity={0.8}
                  >
                    {tick.toFixed(1)}
                  </text>
                </g>
              )
            })}
            
            {/* Highlight points at each epoch change - commented out for now */}
            {/*
            {stackedEpochData.map((point, index) => {
              // Skip the first point (current epoch) as it's always at 0
              if (index === 0 && point.epoch === currentEpoch) return null
              
              // Skip points outside the visible area
              const x = scaleX(point.epoch)
              const y = scaleY(point.totalTokens)
              if (x < 0 || x > graphWidth || y < 0 || y > graphHeight) return null
              
              // Ensure tooltip stays within boundaries
              const tooltipHeight = point.dataPoints.length * 20 + 40
              const tooltipWidth = 200
              const tooltipPos = getTooltipPosition(x, y, tooltipWidth, tooltipHeight)
              
              return (
                <g 
                  key={`stack-point-${index}`}
                  onMouseEnter={() => setHoveredPoint(`epoch-${point.epoch}`)}
                  onMouseLeave={() => setHoveredPoint(null)}
                >
                  <circle
                    cx={x}
                    cy={y}
                    r={5}
                    fill={hoveredPoint === `epoch-${point.epoch}` ? "#fff" : "#60e25e"}
                    stroke="white"
                    strokeWidth={1.5}
                  />
                  
                  {hoveredPoint === `epoch-${point.epoch}` && (
                    <>
                      <rect
                        x={tooltipPos.x}
                        y={tooltipPos.y}
                        width={tooltipWidth}
                        height={tooltipHeight}
                        rx={4}
                        fill="rgba(0,0,0,0.8)"
                      />
                      <text
                        x={tooltipPos.x + 10}
                        y={tooltipPos.y + 20}
                        fill="white"
                        fontSize={12}
                        fontWeight="bold"
                      >
                        Epoch {point.epoch} - Total: {point.totalTokens.toFixed(2)}
                      </text>
                      
                      {point.dataPoints.map((data, i) => (
                        <text
                          key={`tooltip-${data.id}`}
                          x={tooltipPos.x + 10}
                          y={tooltipPos.y + 40 + i * 20}
                          fill="#60e25e"
                          fontSize={11}
                        >
                          Stake {i+1}: {calculateEffectiveStakedTokens(data).toFixed(2)} tokens
                        </text>
                      ))}
                      
                      <line
                        x1={x}
                        y1={y + 5}
                        x2={x}
                        y2={graphHeight}
                        stroke="#60e25e"
                        strokeWidth={1}
                        strokeDasharray="3,3"
                      />
                    </>
                  )}
                </g>
              )
            })}
            */}
          </g>
          
          {/* Axis labels */}
          <text 
            x={width / 2} 
            y={height - 5} 
            textAnchor="middle" 
            fill="white"
            fontSize={14}
          >
            Epoch
          </text>
          <text
            x={15}
            y={height / 2}
            textAnchor="middle"
            fill="white"
            fontSize={14}
            transform={`rotate(-90, 15, ${height / 2})`}
          >
            Staked
          </text>
        </svg>
      </div>
      
      <div className="px-8">
        <div className="flex flex-col gap-3 bg-black/20 rounded-lg p-4 border border-white/5">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setExtended(!extended)}
              className={`px-5 py-2 rounded-lg font-bold transition-all duration-200 ${
                extended 
                  ? "bg-white/20 text-white hover:bg-white/30" 
                  : "bg-[#60e25e] text-[#19191b] hover:bg-[#6eee6c]"
              }`}
              style={{ boxShadow: extended ? "none" : "0px 3px 0px 0 #439e42" }}
              disabled={activeStakes.length === 0}
            >
              {extended ? "Cancel Extend" : "Extend"}
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-white/80 text-sm font-medium">
                +{Math.round(extensionFactor)} epochs
              </span>
              <div className="w-2 h-2 rounded-full bg-[#60e25e] animate-pulse"></div>
            </div>
          </div>
          
          <div className="mt-1">
            <div className="flex justify-between text-xs text-white/70 px-1 mb-1">
              <span>Extension amount</span>
              <span className="font-medium text-[#60e25e]">+{Math.round(extensionFactor)}</span>
            </div>
            <input
              type="range"
              min={minExtensionValue}
              max={maxExtensionValue}
              step="1"
              value={extensionFactor}
              onChange={(e) => setExtensionFactor(parseInt(e.target.value))}
              className="w-full h-2 accent-[#60e25e] rounded-lg appearance-none cursor-pointer bg-white/10"
              aria-label="Extension factor slider"
            />
            <div className="flex justify-between text-xs text-white/60 px-1 mt-1">
              <span>+{minExtensionValue}</span>
              <span>+{Math.round((maxExtensionValue + minExtensionValue) / 2)}</span>
              <span>+{maxExtensionValue}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 